/**
 * Data table with optional row selection and loading skeleton.
 */
import React, { memo } from "react";
import { Box, Checkbox } from "@mui/material";
import DataTable from "react-data-table-component";
import Skeleton from "react-loading-skeleton";
import PropTypes from "prop-types";

import { asArray, asBool, asFunc } from "utils/safeData";

/**
 * Server-paginated data table built on react-data-table-component.
 *
 * @param {{ columns?: Array, data?: Array, loading?: boolean, isSelect?: boolean, onSelectChange?: Function }} props
 */
const CustomTable = (props) => {
  const { columns, data, loading, isSelect, onSelectChange, ...rest } = props;
  const tableColumns = asArray(columns);
  const tableData = asArray(data);
  const isLoading = asBool(loading);
  const selectable = asBool(isSelect);
  const handleSelect = asFunc(onSelectChange);

  /**
   * Forwards selected rows to the parent.
   *
   * @param {{ selectedRows: Array }} param0 Selection payload.
   * @returns {void}
   */
  const handleChange = ({ selectedRows }) => handleSelect(selectedRows);

  return (
    <Box className={`v-table w-full ${tableData.length ? "" : "hidden"}`}>
      <DataTable
        columns={tableColumns}
        data={tableData}
        responsive
        theme="default"
        selectableRowsComponent={Checkbox}
        persistTableHead={true}
        selectableRows={selectable}
        progressPending={isLoading}
        progressComponent={
          <Box className="h-160 w-full">
            <Skeleton count={10} className="w-full h-10 mt-2" />
          </Box>
        }
        fixedHeader
        fixedHeaderScrollHeight="100%"
        paginationServer
        highlightOnHover={true}
        onSelectedRowsChange={handleChange}
        {...rest}
      />
    </Box>
  );
};

CustomTable.defaultProps = {
  columns: [],
  data: [],
  loading: false,
  isSelect: false,
  handlePageChange: () => {},
  handlePageSizeChange: () => {},
  onSelectChange: () => {},
};

CustomTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  isSelect: PropTypes.bool.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handlePageSizeChange: PropTypes.func.isRequired,
  onSelectChange: PropTypes.func.isRequired,
};

/**
 * Memo comparator that skips re-renders when loading and page state are unchanged.
 *
 * @param {Object} prevProps Previous props.
 * @param {Object} nextProps Next props.
 * @returns {boolean} True when the table can skip rendering.
 */
const equalCheck = (prevProps, nextProps) => {
  if (prevProps.loading !== nextProps.loading) return false;
  if (prevProps.page != null && nextProps.page != null && (prevProps.page !== nextProps.page || prevProps.pageSize !== nextProps.pageSize)) {
    return false;
  }
  return true;
};

const BTable = memo(CustomTable, equalCheck);
export default BTable;
